import dayjs from 'dayjs';
import { FC, useCallback, useEffect, useRef } from 'react';

import { replaceOrAddLocale, useLocale } from '@/helpers/LangHelper';
import { useArticleLeadImage } from '@/hooks/useLeadImage';
import { Article } from '@/types/Article';

import Breadcrumb, { BreadcrumbItem } from '../breadcrumb/breadcrumb';
import Tag from '../tag/tag';
import styles from './article-hero.module.scss';

interface ArticleHeroProps {
  article: Article;
}

const ArticleHero: FC<ArticleHeroProps> = (props) => {
  const { article } = props;
  const category = article.categories?.[0];
  const author = article.author;
  const containerRef = useRef<HTMLDivElement>(null);
  const heroImage = useArticleLeadImage(article);

  const { locale } = useLocale();

  const breadcrumbs: BreadcrumbItem[] = [
    { name: 'The Loop', link: '/the-loop' },
    { name: category.name, link: category.categoryUrl },
    { name: article.title, link: '' },
  ];

  const transformDetector = useCallback(() => {
    if (!containerRef || !containerRef.current) return;

    const targetElem = containerRef.current;
    const topOfWindow = window.scrollY;
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    const minContainerWidth = windowWidth <= 768.98 ? 568 : 684;

    if (topOfWindow > 0) {
      targetElem.style.height = minContainerWidth + 'px';
    } else {
      targetElem.style.height = Math.max(minContainerWidth, windowHeight * 0.6) + 'px';
    }
  }, [containerRef]);

  useEffect(() => {
    transformDetector();
    document.addEventListener('scroll', transformDetector);
    return () => document.removeEventListener('scroll', transformDetector);
  }, [transformDetector]);

  return (
    <div ref={containerRef} className={styles.container}>
      <div
        className={styles.background}
        style={{
          backgroundImage: `url(${heroImage})`,
          backgroundPosition: article.leadImagePosition,
        }}
      >
        <div className={styles.mask} />
      </div>
      <div className={styles.content}>
        <div className={styles.breadcrumb}>
          <Breadcrumb type="light" breadcrumbs={breadcrumbs} />
        </div>
        <div className={styles.mainContent}>
          <h1 className={styles.title}>{article.title}</h1>
          <div className={styles.categories}>
            {article.categories.map((category) => (
              <Tag
                link={
                  category.categoryUrl
                    ? replaceOrAddLocale(category.categoryUrl, locale)
                    : undefined
                }
                key={category.id}
              >
                {category.name}
              </Tag>
            ))}
          </div>
          {author ? (
            <div className={styles.infoContainer}>
              <div className={styles.authorContainer}>
                <img className={styles.authorAvatar} src={author?.leadImage} alt="" />
                <div className={styles.authorNameContainer}>
                  <span className={styles.authorBy}>by&nbsp;</span>
                  <span className={styles.authorName}>{article.author?.name}</span>
                </div>
              </div>
              <div className={styles.info}>
                {`${dayjs(article.overrideDate || article.createdDate).format('MMM DD, YYYY')}`}

                {article.readingTime && (
                  <span>
                    &nbsp;&nbsp;&nbsp;<span className={styles.dot}>&#8226;</span>&nbsp;&nbsp;&nbsp;
                    {`${article.readingTime} min read`}
                  </span>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ArticleHero;
